import axios from 'axios';
import { globalConfig } from '../configuration/config';

const CALCULATION_RENDER_POLL_INTERVAL_MS = 2000;
const CALCULATION_RENDER_POLL_FAILURE_LIMIT = 3;
const CALCULATION_RENDER_SUBMISSION_ID_PATTERN =
  /^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$/;
const TERMINAL_RENDER_STATES = new Set([
  'succeeded',
  'partial',
  'failed',
]);

function normalizeText(value) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim();
  return normalizedValue.length > 0 ? normalizedValue : undefined;
}

function getCalculationRenderApiBaseUrl() {
  const configuredBaseUrl = normalizeText(
    globalConfig.config.common.calculationRenderApi,
  );

  if (configuredBaseUrl === undefined) {
    throw new Error('Calculation render API URL is not configured.');
  }

  return `${configuredBaseUrl.replace(/\/+$/, '')}/`;
}

function getCalculationRenderUrl(calculationGuid) {
  const normalizedCalculationGuid = normalizeText(calculationGuid);

  if (normalizedCalculationGuid === undefined) {
    throw new Error('Calculation guid is unavailable.');
  }

  return (
    `${getCalculationRenderApiBaseUrl()}Calculation/` +
    `${encodeURIComponent(normalizedCalculationGuid)}/renders`
  );
}

function waitForNextPoll(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener('abort', abortHandler);
      resolve();
    }, milliseconds);

    function abortHandler() {
      window.clearTimeout(timeoutId);
      signal.removeEventListener('abort', abortHandler);
      reject(new DOMException('Aborted', 'AbortError'));
    }

    signal?.addEventListener('abort', abortHandler, { once: true });
  });
}

export function isCalculationRenderAbortError(error) {
  return error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';
}

export function getCalculationRenderErrorMessageKey(error) {
  switch (error?.response?.status) {
    case 401:
      return 'calculationRender.errors.loginRequired';
    case 403:
      return 'calculationRender.errors.accessDenied';
    case 409:
      return 'calculationRender.errors.activeConflict';
    case 422:
      return 'calculationRender.errors.notRenderable';
    default:
      return 'calculationRender.errors.requestFailed';
  }
}

export async function requestCalculationRender({
  calculationGuid,
  idempotencyKey,
  mode,
  signal,
}) {
  const normalizedIdempotencyKey = normalizeText(idempotencyKey);

  if (normalizedIdempotencyKey === undefined) {
    throw new Error('Calculation render idempotency key is unavailable.');
  }

  const response = await axios.post(
    getCalculationRenderUrl(calculationGuid),
    { force: true, mode },
    {
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': normalizedIdempotencyKey,
      },
      signal,
      withCredentials: true,
    },
  );
  const submissionId = response.data?.submissionId;

  if (
    response.status !== 202 ||
    typeof submissionId !== 'string' ||
    !CALCULATION_RENDER_SUBMISSION_ID_PATTERN.test(submissionId)
  ) {
    throw new Error('Calculation render submission id is unavailable.');
  }

  return response.data;
}

export async function getCalculationRenderStatus({
  calculationGuid,
  signal,
  submissionId,
}) {
  const normalizedSubmissionId = normalizeText(submissionId);

  if (normalizedSubmissionId === undefined) {
    throw new Error('Calculation render submission id is unavailable.');
  }

  const response = await axios.get(
    `${getCalculationRenderUrl(calculationGuid)}/${encodeURIComponent(
      normalizedSubmissionId,
    )}`,
    {
      signal,
      withCredentials: true,
    },
  );

  return response.data;
}

export async function pollCalculationRender({
  calculationGuid,
  onRetryableError,
  onStatusChange,
  signal,
  submissionId,
}) {
  let consecutiveFailures = 0;

  while (true) {
    try {
      const status = await getCalculationRenderStatus({
        calculationGuid,
        signal,
        submissionId,
      });

      consecutiveFailures = 0;
      onStatusChange?.(status);

      if (TERMINAL_RENDER_STATES.has(status?.state)) {
        return status;
      }

      await waitForNextPoll(CALCULATION_RENDER_POLL_INTERVAL_MS, signal);
    } catch (error) {
      if (isCalculationRenderAbortError(error)) {
        throw error;
      }

      consecutiveFailures += 1;
      onRetryableError?.({
        attempt: consecutiveFailures,
        error,
      });

      if (consecutiveFailures >= CALCULATION_RENDER_POLL_FAILURE_LIMIT) {
        throw error;
      }

      await waitForNextPoll(CALCULATION_RENDER_POLL_INTERVAL_MS, signal);
    }
  }
}

export async function runCalculationRender({
  calculationGuid,
  idempotencyKey,
  mode,
  onSubmissionCreated,
  onRetryableError,
  onStatusChange,
  signal,
}) {
  const submission = await requestCalculationRender({
    calculationGuid,
    idempotencyKey,
    mode,
    signal,
  });

  onSubmissionCreated?.(submission);

  return pollCalculationRender({
    calculationGuid,
    onRetryableError,
    onStatusChange,
    signal,
    submissionId: submission.submissionId,
  });
}
